import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import gamificationService from '../../services/gamificationService';
import prestigeService from '../../services/prestigeService';
import dailyStatsService from '../../services/dailyStatsService';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';

/**
 * LumoStatsBar - Mini dashboard showing progress metrics in Lumo chat
 * Displays: streak, level, XP progress, prestige badges, today's XP
 */
const LumoStatsBar = ({ isCollapsed: initialCollapsed = false }) => {
  const { profile } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [xpAnimation, setXpAnimation] = useState(null);
  const [stats, setStats] = useState({
    streak: 0,
    level: 1,
    currentXp: 0,
    xpToNextLevel: 100,
    todayXp: 0,
    prestigeLevel: 0,
    prestigeBadges: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();

    // Subscribe to real-time XP updates
    const unsubscribe = dailyStatsService.subscribe((sessionStats) => {
      const xpDiff = sessionStats.todayXp - stats.todayXp;
      
      // Show XP gain animation if XP increased
      if (xpDiff > 0) {
        setXpAnimation(xpDiff);
        setTimeout(() => setXpAnimation(null), 2000);
      }

      setStats(prev => ({
        ...prev,
        todayXp: sessionStats.todayXp,
        // Also update current XP and level from profile
        currentXp: (profile?.xp || 0) % prev.xpToNextLevel,
        level: profile?.level || prev.level
      }));
    });

    return () => unsubscribe();
  }, [profile]);

  const loadStats = async () => {
    try {
      setLoading(true);

      // Fetch streak
      const streakData = await gamificationService.getStreak();
      
      // Fetch prestige info
      const prestigeHistory = await prestigeService.getPrestigeHistory();
      const badges = await prestigeService.getPrestigeBadges();

      // Calculate XP progress
      const level = profile?.level || 1;
      const currentXp = profile?.xp || 0;
      const xpToNextLevel = calculateXPForLevel(level + 1) - calculateXPForLevel(level);
      const xpProgress = currentXp % xpToNextLevel;

      // Get today's XP (real-time from session tracker)
      const todayXp = dailyStatsService.getStats().todayXp;

      setStats({
        streak: streakData?.currentStreak || 0,
        level,
        currentXp: xpProgress,
        xpToNextLevel,
        todayXp,
        prestigeLevel: profile?.prestige_level || 0,
        prestigeBadges: badges || []
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate XP required for a level (same formula from gamificationService)
  const calculateXPForLevel = (level) => {
    return Math.floor(100 * Math.pow(1.5, level - 1));
  };

  const xpPercentage = (stats.currentXp / stats.xpToNextLevel) * 100;

  if (loading) {
    return (
      <div className="px-4 py-3 border-b border-border bg-background/50 animate-pulse">
        <div className="h-12 bg-accent/20 rounded" />
      </div>
    );
  }

  return (
    <div className="border-b border-border bg-gradient-to-r from-primary/5 to-accent/5">
      {/* Collapse/Expand Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-accent/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon 
            name="TrendingUp" 
            size={16} 
            className="text-primary"
          />
          <span className="text-xs font-semibold text-foreground">Your Progress</span>
        </div>
        <Icon 
          name={isCollapsed ? "ChevronDown" : "ChevronUp"} 
          size={16} 
          className="text-muted-foreground"
        />
      </button>

      {/* Stats Content */}
      {!isCollapsed && (
        <div className="px-4 pb-3 space-y-3">
          {/* Top Row: Streak + Level */}
          <div className="flex items-center justify-between gap-4">
            {/* Streak */}
            <div className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-1.5">
                <Icon name="Flame" size={18} className="text-warning" />
                <span className="text-lg font-bold text-foreground">{stats.streak}</span>
              </div>
              <span className="text-xs text-muted-foreground">day streak</span>
            </div>

            {/* Level */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              <span className="text-xs text-muted-foreground">Level</span>
              <div className="flex items-center gap-1.5">
                <Icon name="Award" size={18} className="text-primary" />
                <span className="text-lg font-bold text-foreground">{stats.level}</span>
              </div>
            </div>
          </div>

          {/* XP Progress Bar */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">XP to Level {stats.level + 1}</span>
              <span className="font-medium text-foreground">
                {stats.currentXp} / {stats.xpToNextLevel}
              </span>
            </div>
            <div className="h-2 bg-accent/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500 ease-out"
                style={{ width: `${Math.min(xpPercentage, 100)}%` }}
              />
            </div>
          </div>

          {/* Bottom Row: Today's XP + Prestige */}
          <div className="flex items-center justify-between gap-4 pt-1">
            {/* Today's XP with animation */}
            <div className="flex items-center gap-1.5 relative">
              <Icon name="Zap" size={14} className="text-success" />
              <span className="text-xs text-muted-foreground">Today:</span>
              <span className="text-xs font-semibold text-success">+{stats.todayXp} XP</span>
              
              {/* XP Gain Animation */}
              {xpAnimation && (
                <span className="absolute -top-6 left-0 text-xs font-bold text-success animate-float-up">
                  +{xpAnimation} XP
                </span>
              )}
            </div>

            {/* Prestige Badges */}
            {stats.prestigeLevel > 0 && (
              <div className="flex items-center gap-1.5">
                {stats.prestigeBadges.slice(0, 3).map((badge, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold",
                      badge.tier === 'bronze' && "bg-gradient-to-br from-orange-600 to-orange-400 text-white",
                      badge.tier === 'silver' && "bg-gradient-to-br from-gray-400 to-gray-200 text-gray-800",
                      badge.tier === 'gold' && "bg-gradient-to-br from-yellow-500 to-yellow-300 text-yellow-900"
                    )}
                    title={`Prestige ${badge.tier.toUpperCase()}`}
                  >
                    P
                  </div>
                ))}
                {stats.prestigeBadges.length > 3 && (
                  <span className="text-xs text-muted-foreground">+{stats.prestigeBadges.length - 3}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LumoStatsBar;
